import React from "react";
import styled from "styled-components";

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepperButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid var(--soremo-border);
  background: #fff;
  color: var(--soremo-blue);
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s;

  &:hover:not(:disabled) {
    background: rgba(0, 154, 206, 0.08);
    border-color: var(--soremo-blue);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const StyledInput = styled.input`
  width: 80px;
  text-align: center;
  padding: 8px 4px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  font-family: "A+mfCv-AXISラウンド 50 L StdN", "M PLUS 1p", sans-serif;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: var(--soremo-blue);
    box-shadow: 0 0 0 2px rgba(0, 154, 206, 0.1);
  }

  &:invalid {
    border-color: var(--soremo-green);
  }
`;

export interface NumberStepperProps {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  "aria-label"?: string;
}

const NumberStepper: React.FC<NumberStepperProps> = ({
  value,
  min,
  max,
  onChange,
  "aria-label": ariaLabel,
}) => {
  const handleIncrement = () => {
    const next = Math.min(max, value + 1);
    if (next !== value) onChange(next);
  };

  const handleDecrement = () => {
    const next = Math.max(min, value - 1);
    if (next !== value) onChange(next);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (raw === "") {
      onChange(min);
      return;
    }
    const num = parseInt(raw, 10);
    if (!isNaN(num)) {
      const clamped = Math.max(min, Math.min(max, num));
      onChange(clamped);
    }
  };

  return (
    <Container>
      <StepperButton
        type="button"
        onClick={handleDecrement}
        disabled={value <= min}
        aria-label={ariaLabel ? `${ariaLabel}を減らす` : "減らす"}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 20 }}>remove</span>
      </StepperButton>
      <StyledInput
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={String(value)}
        onChange={handleInputChange}
        aria-label={ariaLabel ? `${ariaLabel}の値` : undefined}
      />
      <StepperButton
        type="button"
        onClick={handleIncrement}
        disabled={value >= max}
        aria-label={ariaLabel ? `${ariaLabel}を増やす` : "増やす"}
      >
        <span className="material-symbols-rounded" style={{ fontSize: 20 }}>add</span>
      </StepperButton>
    </Container>
  );
};

export default NumberStepper;
